import { z } from 'zod'

export const CoproductionStatusSchema = z.enum(['PENDENTE', 'ATIVO', 'REMOVIDO'])
export type CoproductionStatus = z.infer<typeof CoproductionStatusSchema>

export const CoproductionInviteSchema = z.object({
  id: z.string(),
  productId: z.string(),
  producerId: z.string(),
  coProducerEmail: z.string(),
  coProducerId: z.string().optional(),
  percentage: z.string(),
  token: z.string(),
  status: CoproductionStatusSchema,
  product: z.object({ name: z.string() }).optional(),
  producer: z.object({ name: z.string() }).optional(),
  acceptedAt: z.string().optional(),
  deletedAt: z.string().optional(),
  expiresAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type CoproductionInvite = z.infer<typeof CoproductionInviteSchema>

const CoproductionPercentageSchema = z
  .string()
  .regex(/^\d+(\.\d{1,3})?$/, 'percentual inválido')
  .refine((value) => {
    const numeric = Number(value)
    return numeric >= 1 && numeric <= 99
  }, 'percentual do coprodutor deve ficar entre 1 e 99')

export const CreateCoproductionInputSchema = z.object({
  productId: z.string().min(1),
  coProducerEmail: z.string().email(),
  percentage: CoproductionPercentageSchema,
})
export type CreateCoproductionInput = z.infer<typeof CreateCoproductionInputSchema>

export const UpdateCoproductionPercentageInputSchema = z.object({
  percentage: CoproductionPercentageSchema,
})
export type UpdateCoproductionPercentageInput = z.infer<
  typeof UpdateCoproductionPercentageInputSchema
>

export const COPRODUCER_LOCAL_ID_PREFIX = 'copro_'

export function isLocalCoproducerId(id: string) {
  return id.startsWith(COPRODUCER_LOCAL_ID_PREFIX)
}

export type CoproducerDraft = {
  id: string
  email: string
  percentage: string
  status: CoproductionStatus
}

export type CoproductionSyncAction =
  | { kind: 'noop' }
  | { kind: 'create'; coProducerEmail: string; percentage: string }
  | { kind: 'update'; id: string; percentage: string }
  | { kind: 'replace'; removeId: string; coProducerEmail: string; percentage: string }
  | { kind: 'remove'; id: string }

export function planCoproductionSync(
  next: CoproducerDraft | undefined,
  previous: CoproducerDraft | undefined,
): CoproductionSyncAction {
  const serverId = previous && !isLocalCoproducerId(previous.id) ? previous.id : undefined
  const email = (next?.email ?? '').trim()

  if (email === '') {
    return serverId !== undefined ? { kind: 'remove', id: serverId } : { kind: 'noop' }
  }

  const percentage = next?.percentage ?? ''

  if (serverId === undefined) {
    return { kind: 'create', coProducerEmail: email, percentage }
  }

  const previousEmail = (previous?.email ?? '').trim()
  const emailChanged = previousEmail.toLowerCase() !== email.toLowerCase()
  const percentageChanged = (previous?.percentage ?? '') !== percentage
  const canPatchPercentageInPlace = previous?.status === 'ATIVO'

  if (emailChanged || (percentageChanged && !canPatchPercentageInPlace)) {
    return { kind: 'replace', removeId: serverId, coProducerEmail: email, percentage }
  }

  if (percentageChanged) {
    return { kind: 'update', id: serverId, percentage }
  }

  return { kind: 'noop' }
}

export function inviteToCoproducer(invite: CoproductionInvite): CoproducerDraft {
  return {
    id: invite.id,
    email: invite.coProducerEmail,
    percentage: invite.percentage,
    status: invite.status,
  }
}

const coproductionStatusLabels: Record<CoproductionStatus, string> = {
  PENDENTE: 'Convite pendente',
  ATIVO: 'Coprodutor ativo',
  REMOVIDO: 'Removido',
}

export function coproductionStatusLabel(status: CoproductionStatus) {
  return coproductionStatusLabels[status]
}
